export type Testament = 'OT' | 'NT';
export type ScrollAlign = 'center' | 'top';
export type NavHandedness = 'left' | 'right';
export const STREAM_PRIME_RADIUS = 4;
export const READER_WORKSPACE_SNAPSHOT_VERSION = 3;

export type UpdateCheckState =
  | { state: 'idle' }
  | { state: 'checking' }
  | { state: 'up-to-date' }
  | { state: 'error'; message: string };

export interface BookSummary {
  id: number;
  name: string;
  testament: Testament;
  chapterCount: number;
}

export interface ChapterRef {
  key: string;
  bookId: number;
  bookName: string;
  testament: Testament;
  chapter: number;
  verseCount: number;
}

export interface Verse {
  verse: number;
  text: string;
  greekText?: string | null;
  hebrewText?: string | null;
  lxxText?: string | null;
  crossReferences?: CrossReference[] | null;
}

export interface ChapterData extends ChapterRef {
  verses: Verse[];
}

export interface CrossReference {
  id: number;
  bookId: number;
  bookName: string;
  chapter: number;
  verse: number;
  verseEnd: number | null;
  label: string;
  text: string;
}

export interface CrossReferenceNavigation {
  bookId: number;
  chapter: number;
  verse: number;
}

export interface ReaderLocation {
  bookId: number;
  bookName: string;
  chapter: number;
  targetVerse: number | null;
  align: ScrollAlign;
}

export interface ReaderHistoryState extends ReaderLocation {
  kjvreader: true;
  restoreMode: 'scroll' | 'target';
  activeStreamIndex: number;
  streams: ReaderStreamSnapshot[];
}

export interface SearchResult {
  bookId: number;
  bookName: string;
  chapter: number;
  verse: number;
  text: string;
}

export interface VerseHighlight {
  bookId: number;
  chapter: number;
  verse: number;
  nonce: number;
}

export type ReaderStreamSequenceKind = 'canonical' | 'fixed-list';

export interface StreamChapterItem {
  key: string;
  catalogIndex: number;
  bookId: number;
  chapter: number;
  label?: string | null;
}

export interface ReaderStreamSequence {
  kind: ReaderStreamSequenceKind;
  title: string | null;
  source: string | null;
  items: StreamChapterItem[];
}

export interface ReaderStreamSequenceSnapshot {
  kind: ReaderStreamSequenceKind;
  title: string | null;
  source: string | null;
  items?: Array<{
    bookId: number;
    chapter: number;
    label?: string | null;
  }>;
}

export interface ChapterListStreamInput {
  bookId: number;
  chapter: number;
  label?: string | null;
}

export interface HornerProgressState {
  day: number;
  readKeys: string[];
}

export interface NavigationTarget {
  itemIndex: number;
  align: ScrollAlign;
  targetVerse: number | null;
}

export interface StreamNavigationRequest {
  nonce: number;
  target: NavigationTarget;
  behavior: ScrollBehavior;
  restoreScrollTop: number | null;
}

export interface ReaderStreamSnapshot {
  id: string;
  bookId: number;
  chapter: number;
  currentItemIndex: number;
  sequence: ReaderStreamSequenceSnapshot;
  scrollTop: number;
}

export interface ReaderWorkspaceSnapshot {
  version: typeof READER_WORKSPACE_SNAPSHOT_VERSION;
  activeStreamIndex: number;
  streams: ReaderStreamSnapshot[];
}
